import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Leaf, Star } from "lucide-react";
import { MaskLine } from "./motion";
import { PrimaryButton, GhostButton } from "./ui";
import { IMAGES } from "./data";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const cardY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative overflow-hidden bg-gradient-to-b from-[#eef2fc] via-white to-white pt-32 lg:pt-40"
    >
      {/* soft blue glow */}
      <div className="pointer-events-none absolute -left-40 -top-40 h-96 w-96 rounded-full bg-[#2340b5]/10 blur-3xl" />
      <div className="pointer-events-none absolute right-0 top-20 h-80 w-80 rounded-full bg-[#fdb913]/10 blur-3xl" />

      <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-12 px-6 pb-20 lg:grid-cols-2 lg:gap-16 lg:px-12 lg:pb-28">
        {/* Left copy */}
        <div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full bg-[#2340b5]/8 px-4 py-1.5 font-display text-xs font-semibold uppercase tracking-[0.18em] text-[#2340b5]"
          >
            <Leaf className="h-3.5 w-3.5" strokeWidth={2} /> Tagging along helps the environment
          </motion.p>

          <h1 className="font-display text-5xl font-extrabold leading-[0.95] tracking-tighter text-[#12142b] sm:text-6xl lg:text-7xl">
            <MaskLine delay={0.15}>Share the</MaskLine>
            <MaskLine delay={0.28}>
              road. <span className="text-[#2340b5]">Split</span>
            </MaskLine>
            <MaskLine delay={0.41}>the cost.</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-8 max-w-md text-base leading-relaxed text-[#565b73] lg:text-lg"
          >
            SafeRide connects verified drivers with riders heading the same way across Canada.
            Comfortable, affordable inter-city travel — download the app to book your seat.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <PrimaryButton testId="hero-download-btn">Get the App</PrimaryButton>
            <GhostButton
              testId="hero-learn-btn"
              onClick={() => document.getElementById("how")?.scrollIntoView({ behavior: "smooth" })}
            >
              Learn More
            </GhostButton>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-10 flex items-center gap-6 text-sm text-[#565b73]"
          >
            <span className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-[#2340b5]" strokeWidth={2} /> Verified drivers
            </span>
            <span className="flex items-center gap-2">
              <Star className="h-4 w-4 fill-[#fdb913] text-[#fdb913]" /> 4.9 average rating
            </span>
          </motion.div>
        </div>

        {/* Right visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <div className="relative overflow-hidden rounded-[2rem] shadow-2xl shadow-[#12142b]/10">
            <motion.img
              style={{ y: imgY }}
              src={IMAGES.hero}
              alt="Scenic road trip through the mountains"
              className="aspect-[4/5] w-full scale-110 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#12142b]/30 to-transparent" />
          </div>

          {/* Floating stat card */}
          <motion.div
            style={{ y: cardY }}
            className="absolute -bottom-6 -left-4 rounded-2xl border border-[#12142b]/5 bg-white/95 p-5 shadow-xl backdrop-blur lg:-left-8"
          >
            <p className="font-display text-3xl font-black text-[#12142b]">$0</p>
            <p className="mt-1 text-xs font-medium text-[#565b73]">payout fees for drivers</p>
          </motion.div>

          <motion.div
            style={{ y: imgY }}
            className="absolute -right-3 top-8 flex items-center gap-2 rounded-full border border-[#12142b]/5 bg-white/95 px-4 py-2 shadow-xl backdrop-blur lg:-right-6"
          >
            <span className="grid h-8 w-8 place-items-center rounded-full bg-[#e4002b]/10">
              <Leaf className="h-4 w-4 text-[#e4002b]" strokeWidth={2} />
            </span>
            <span className="text-xs font-semibold text-[#12142b]">1.2M kg CO₂ saved</span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
